import {
  CellClassParams,
  CellDoubleClickedEvent,
  CellFocusedEvent,
  CellKeyDownEvent,
  CellStyle,
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  ITooltipParams,
  RowNode,
  SortChangedEvent,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { DOCUMENT } from '@angular/common'
import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { FormControl, ValidationErrors, Validators } from '@angular/forms'
import {
  heliosDefaultColDef,
  heliosDefaultColWidth,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import {
  ExternalTransaction,
  TranCategory,
  TransactionTypeEnum,
  TransactionTypeKey,
} from '@helios/common-api'
import { BlankSessionService } from '@helios/sessions'
import {
  AccountInfo,
  RootState,
  selectActiveSessionIdentifier,
  selectActiveSessionReconDate,
  selectActualReconDate,
  selectFirstFocusedAccountInfo,
  selectFirstLoadedAccountId,
  selectLegacyUserId,
  selectSelectedReconDate,
} from '@helios/state-management'
import { TelemetryService } from '@helios/telemetry'
import {
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import {
  NgbActiveModal,
  NgbModal,
  NgbModalOptions,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { AgGridApi, NfTooltipParams } from '@northfork/ag-grid'
import {
  InlineEditDataType,
  ModalLinkRendererParams,
  NgGridComponents,
  getInlineEditColumnType,
} from '@northfork/ag-grid-angular'
import { InlineEditDisplayOptions, ToastService } from '@northfork/ng-basics'
import { compareAsc, isAfter, parseISO } from 'date-fns'
import { Observable, Subject } from 'rxjs'
import { debounceTime, take, takeUntil } from 'rxjs/operators'
import { OrigUnitsRequest } from '../../../model/bulk-transaction/bulk-direct-transfer/bulk-direct-transfer.model'
import {
  BulkTransactionModalInput,
  BulkTransactionModalMode,
  BulkTransferPositionData,
} from '../../../model/bulk-transaction/bulk-popup-input-model'
import {
  BulkSecurityTransferRow,
  securityField,
} from '../../../model/bulk-transaction/bulk-security-transfer/bulk-security-transfer.model'
import {
  BulkSleeveSimpleAccounts,
  SleeveSimple,
} from '../../../model/bulk-transaction/bulk-sleeve-simple-accounts.model'
import {
  AdditionalDetailsSecurityTransfer,
  BulkSecurityTransferTransaction,
  ClearwaterTransactionDetailResponse,
  MetaDataTransaction,
  TransactionRequestWrapper,
} from '../../../model/bulk-transaction/bulk-transaction'
import { BulkTransactionRequest } from '../../../model/bulk-transaction/bulk-transaction.model'
import { Position } from '../../../model/bulk-transaction/bulk-transfer-out-all-positions-model'
import { BulkSecurityTransferValidationView } from '../../../model/bulk-transaction/bulk-validation-view.model'
import { CounterParty } from '../../../model/counter-party.model'
import { Currency } from '../../../model/currency.model'
import { InventoryType, inventories } from '../../../model/inventory-type.model'
import {
  Basis,
  MvtBasis,
  MvtBasisSelection,
} from '../../../model/mvt-basis.model'
import { PricingRequest, PricingResponse } from '../../../model/pricing.model'
import { SecurityMasterData } from '../../../model/security.model'
import { ExternalUnmatchedTransaction } from '../../../model/single-transaction/external-unmatched-transaction'
import {
  MarketValueTransferModel,
  MvtData,
} from '../../../model/single-transaction/market-value-transfer.model'
import { Transaction } from '../../../model/single-transaction/transaction'
import { TranCategoryUrl } from '../../../model/transaction-category.model'
import {
  ErrorSeverity,
  ValidationInfo,
} from '../../../model/validation-response.model'
import { BulkDeleteService } from '../../../services/bulk-transaction/bulk-delete.service'
import { PasteService } from '../../../services/bulk-transaction/paste.service'
import { CopyService } from '../../../services/copy.service'
import { CreateEditTransactionService } from '../../../services/create-edit-transaction.service'
import { ModalFocusService } from '../../../services/modal-focus.service'
import {
  isBulkCopyModal,
  isBulkCreateModal,
  isBulkEditModal,
  isBulkExternalModal,
} from '../../../util/bulk-popup-input-utils'
import {
  BULK_DIALOG_EVENT_NAME,
  buildMvtBasisSelection,
  bulkMetricsDetail,
  bulkRequestMode,
  convertBulkResponseKeyToPricingRequest,
  customSortComparatorBulk,
  customSortComparatorBulkNumeric,
  customSortComparatorBulkValidatorColumn,
  fillHandleDoubleClickHandler,
  getAccountCellParams,
  onSortChange,
  showOrderIDColumn,
  showTranIdColumn,
} from '../../../util/bulk-specific-common-utils'
import {
  mapBulkExternalTransaction,
  mapLoadToBulkTransaction,
} from '../../../util/bulk-transaction-wrapper-util'
import {
  activateDialog,
  deduceValidityStatusCellClass,
  emptySelectedCellsOnValidKeyPress,
  getDefaultUnknownCounterParty,
  getFieldValidatorFormatter,
  getTransactionTypeCodeFromKey,
  getTransactionTypeKeyFromCode,
  getTransactionTypeNameFromCode,
  isDialogComponentInActive,
  markRowsMissingAccount,
} from '../../../util/common-utils'
import { TransactionConstants } from '../../../util/constants'
import { BvMvBasisComponent } from '../../bv-mv-basis-selector/bv-mv-basis-selector.component'
import { BulkTransactionModalResponse } from '../bulk-transaction-modal-response'
import { ErrorTooltipComponent } from '../error-Tooltip/error-tooltip.component'
import { BulkTransactionsSaveConfirmationModalComponent } from '../bulk-transactions-confirm-button-modal/bulk-transactions-confirm-button-modal.component'

@Component({
  selector: 'hs-bulk-security-transfer-out',
  templateUrl: './bulk-security-transfer-out.component.html',
  styleUrls: ['./bulk-security-transfer-out.component.scss'],
  providers: [PasteService, CopyService, BulkDeleteService],
})
export class BulkSecurityTransferOutComponent implements OnInit, OnDestroy {
  @Input() transInput?: BulkTransactionModalInput

  private readonly onPasteEndBinding = this.onPasteEnd.bind(this)
  private readonly adjustTransactionsPostDeleteBinding =
    this.adjustTransactionsPostDelete.bind(this)

  pricingRequestToRowIds: Map<string, string[]> = new Map<string, string[]>()
  mvtDataLoaded: Map<string, MvtBasis> = new Map<string, MvtBasis>()
  loadingBasis = false
  activeInstances: NgbModalRef[] = []
  compId = 'bulk-security-transfer-out' + Math.random()

  constructor(
    public activeModal: NgbActiveModal,
    public toastService: ToastService,
    readonly store: Store<RootState>,
    readonly modalService: NgbModal,
    public bulkTransactionService: CreateEditTransactionService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly blankSessionService: BlankSessionService,
    private readonly pasteService: PasteService,
    private readonly copyService: CopyService,
    private readonly bulkDeleteService: BulkDeleteService,
    private readonly userPreferencesService: UserPreferencesService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly telemetryService: TelemetryService,
    private readonly elRef: ElementRef,
    private readonly modalFocusService: ModalFocusService,
  ) {
    this.store
      .select(selectActualReconDate)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((reconDate?: Date) => {
        this.reconDate = reconDate
      })
    this.store
      .select(selectActiveSessionReconDate)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((sessionReconDate?: Date) => {
        this.sessionReconDate = sessionReconDate
      })
    this.store
      .select(selectSelectedReconDate)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((selectedReconDate?: Date) => {
        this.selectedReconDate = selectedReconDate
      })
    this.store
      .select(selectActiveSessionIdentifier)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((sessionId?: string) => {
        this.sessionId = sessionId
      })
    this.store
      .select(selectLegacyUserId)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((userId?: number) => {
        this.userId = userId
      })
    this.userPreferencesService
      .getBulkDialogSortPromptPreference()
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe(sortable => {
        this.sortable = sortable
        if (this.gridOptions.defaultColDef) {
          this.gridOptions.defaultColDef.sortable = sortable
          this.gridOptions.defaultColDef.comparator = customSortComparatorBulk
        }
      })
    this.modalService.activeInstances
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(activeInstances => {
        this.activeInstances = activeInstances
      })
  }

  private readonly transTypesIn = ['TRNO', 'EXOT']
  missingTranRowList: Set<string> = new Set<string>()
  sortable = false
  rowData: BulkSecurityTransferRow[] = []
  currencyList: Currency[] = []
  getFieldValidatorFormatter = getFieldValidatorFormatter
  colDefs = this.createColumnDefs()
  gridOptions: GridOptions = this.createGridOptions()
  isDataValid = true
  isCommonValidationError = false
  disableSaveValidRows = false
  isValidating = false
  formSubmitting = false
  allDataValidated = false
  lastPastedRowIds: string[] = []
  lastColumnIdsPasted: Set<string> = new Set<string>()
  gridApi?: AgGridApi
  note = new FormControl('')
  reconDate?: Date
  sessionReconDate?: Date
  selectedReconDate?: Date
  sessionId?: string
  private userId?: number
  counterPartyList: CounterParty[] = []
  cellLevelErrorMessageMap = new Map<string, string[]>()
  cellLevelWarningMessageMap = new Map<string, string[]>()
  noteWarning: ValidationErrors | null = null
  currentNoteError: ValidationErrors | null =
    TransactionConstants.DEFAULT_NOTE_VALIDATION_ERROR
  genericErrors: string[] = []
  genericWarnings: string[] = []
  loadingDialog = false
  title = 'Bulk Security Transfer Create (Out)'
  currentRowIndex = 0
  originalTransactions: BulkSecurityTransferTransaction[] = []
  externalTransaction: ExternalTransaction[] = []
  bulkExternalTransactionDetail: ExternalUnmatchedTransaction[] = []
  isEdit = false
  pastePostProcessingInProgress = false
  deletePostProcessingInProgress = false
  private readonly destroySubject$ = new Subject<void>()
  disableValidate = true
  isDeriving = false
  accountList: Set<number> = new Set<number>()
  isCollateralMap: { [key: number]: boolean } = {}
  basisMap: { [key: number]: Basis[] } = {}

  securityMasterData: Map<string, SecurityMasterData> = new Map<
    string,
    SecurityMasterData
  >()
  clearwaterTransactionDetail: { [s: string]: Transaction } = {}

  mvtMapByTransaction: {
    [key: string]: { [key: string]: MvtData }
  } = {}

  sessionAccountInfo?: AccountInfo
  bulkSleeveSimpleAccount: BulkSleeveSimpleAccounts[] = []
  sleeveSimpleAccount: SleeveSimple[] = []
  sleeveAccountIds: number[] = []
  cusipToSecurityMap = new Map<string, number>()
  loadingText = TransactionConstants.LOAD_TRANSACTION
  errorText = ''
  errorHeader: string | undefined
  errorDetails: unknown
  clientLotIdMapBulkPanel: Map<string, string> = new Map<string, string>()
  initialClientLotIdMap: Map<string, string> = new Map<string, string>()
  reprocessTransfers = new FormControl(false)
  showConfirmButtonModal = false

  async ngOnInit() {
    const startTime = Date.now()

    activateDialog(this.activeInstances, this.compId, this.elRef)

    this.modalFocusService
      .outsideFocus(this.elRef)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => this.disableGridFocus())

    this.note.valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroySubject$))
      .subscribe(() => {
        this.setNoteErrors()
      })

    if (this.transInput) {
      showTranIdColumn(this.gridOptions, this.transInput, this.sortable)
      if (
        isBulkEditModal(this.transInput) &&
        this.transInput.transactions?.[0].orderId
      ) {
        showOrderIDColumn(this.gridOptions, this.sortable)
      }
    }

    try {
      await this.init()
    } catch (error) {
      this.errorText = TransactionConstants.ERROR_LOADING_TRANSACTION
      this.errorDetails = error.error
    }
    this.note.markAsTouched()
    this.note.setErrors(this.currentNoteError)
    const now = Date.now()
    const [count, type] = bulkMetricsDetail(
      this.rowData.length,
      this.transInput,
    )
    this.telemetryService.emitCustomEvent(BULK_DIALOG_EVENT_NAME, {
      dialogName: 'Security-Transfer-OUT-' + type,
      durationMs: now - startTime,
      tranCount: count,
    })
  }

  private async init() {
    this.loadingDialog = true
    this.document.addEventListener('pasteDone', this.onPasteEndBinding)
    this.document.addEventListener(
      'deleteSuccessful',
      this.adjustTransactionsPostDeleteBinding,
    )

    if (this.transInput?.extTranId) {
      await this.loadExtTransactionDetailBulk(this.transInput.extTranId)
    }

    if (this.transInput?.bulkStatus) {
      this.transInput.externalTransaction = mapBulkExternalTransaction(
        this.bulkExternalTransactionDetail,
        this.transInput.bulkStatus,
      )
    }

    if (this.transInput?.cwTranId) {
      await this.loadTransactionDetailBulk(
        this.transInput?.cwTranId,
        this.transInput.securityId ?? [],
      )
      this.transInput.transactions = mapLoadToBulkTransaction(
        this.clearwaterTransactionDetail,
      )
    } else if (
      !isBulkCopyModal(this.transInput) &&
      this.transInput?.transactions
    ) {
      this.originalTransactions = this.transInput
        ?.transactions as BulkSecurityTransferTransaction[]
      await this.loadAdditionalDetailBulk(
        this.originalTransactions.map(
          origTran => origTran.legacyOrigTranId ?? 0,
        ),
        this.originalTransactions.map(origTran => origTran.legacyTranId ?? 0),
        this.originalTransactions.map(origTran => origTran.securityId ?? 0),
        [
          ...new Set(
            this.originalTransactions.map(origTran => origTran.accountId),
          ),
        ],
      )
    }
    if (
      !isBulkCopyModal(this.transInput) &&
      (this.transInput?.transactions || this.transInput?.externalTransaction)
    ) {
      this.missingTranRowList.clear()
      this.disableValidate = false
      this.isEdit = true
      this.pasteService.setIsEdit()

      if (isBulkEditModal(this.transInput)) {
        this.title = 'Bulk Security Transfer Edit (Out)'
        this.originalTransactions = this.transInput
          ?.transactions as BulkSecurityTransferTransaction[]

        await this.loadDataEdit(
          new Set(
            this.originalTransactions.map(origTran => origTran.accountId),
          ),
        )

        this.originalTransactions.forEach((loadTrans, index) => {
          const bulkTransfer: BulkSecurityTransferRow =
            this.generateDataEntryFromTransferEdit(loadTrans, index)
          this.rowData.push(bulkTransfer)

          if (bulkTransfer.transactionId)
            this.initialClientLotIdMap.set(
              bulkTransfer.transactionId,
              bulkTransfer.clientLotId ?? '',
            )
        })
      } else if (isBulkExternalModal(this.transInput)) {
        this.title = 'Bulk Security Transfer Post (Out)'
        this.externalTransaction = this.transInput?.externalTransaction ?? []

        if (!this.transInput?.extTranId) {
          const uniqueAccountIdExternal = [
            ...new Set(this.externalTransaction.map(obj => obj.accountId)),
          ]
          await this.loadNonSessionAccountTypeInfo(uniqueAccountIdExternal)
        }
        this.externalTransaction.forEach((extTrans, index) => {
          this.rowData.push(
            this.generateDataEntryFromExternalTransfer(extTrans, index),
          )
        })
      }
    } else if (this.transInput?.positionData) {
      await this.processDataForTransferAllPositions(
        this.transInput.positionData,
      )
    } else if (this.transInput?.transferAllPositionsRequest) {
      let transferAllPositionData: Position[] = []
      const positionData: BulkTransferPositionData[] = []
      const uniquePositions: Set<string> = new Set<string>()
      await this.bulkTransactionService
        .loadAllPositionsForTransferOut(
          this.transInput?.transferAllPositionsRequest,
        )
        .toPromise()
        .then(response => {
          transferAllPositionData = response
        })
      transferAllPositionData.forEach(data => {
        const key = `${data.secId}-${data.acctId}-${data.inventoryType}-${data.collateralBroker}`
        if (!uniquePositions.has(key)) {
          uniquePositions.add(key)
          positionData.push(
            this.buildTransferAllPositionData(
              data,
            ) as unknown as BulkTransferPositionData,
          )
        }
      })
      await this.processDataForTransferAllPositions(positionData)
    } else if (isBulkCopyModal(this.transInput)) {
      this.missingTranRowList.clear()
      this.disableValidate = false
      this.originalTransactions = this.transInput
        ?.transactions as BulkSecurityTransferTransaction[]

      this.originalTransactions.forEach((loadTrans, index) => {
        const bulkTransfer: BulkSecurityTransferRow =
          this.generateDataEntryFromTransferEdit(loadTrans, index)
        this.rowData.push(bulkTransfer)
      })

      this.rowData.push(
        this.generateEmptyDataEntry(false, this.rowData.length + 1),
      )
    } else {
      this.createFreshRowData()
    }

    this.gridApi?.api.applyTransaction({ add: this.rowData })
    this.loadBasis()
    this.setSleeveSimpleAccountIdsPerRow()

    this.loadingDialog = false
  }

  async processDataForTransferAllPositions(
    positionData: BulkTransferPositionData[],
  ) {
    this.missingTranRowList.clear()
    this.disableValidate = false
    positionData.forEach((data, index) => {
      const positionTransferData = this.generateDataEntryFromPositionTransfer(
        data,
        index,
      )
      this.rowData.push(positionTransferData)
    })
  }

  buildTransferAllPositionData(data: Position) {
    return {
      sessionReconDate: this.sessionReconDate,
      assetId: data.secIdentifier,
      accountId: data.acctId,
      units: data.cwanOriginalUnits ?? '',
      securityId: data.secId,
      collateralBroker: data.collateralBroker,
      inventoryType: data.inventoryType ?? 'LONG',
    }
  }

  setSleeveSimpleAccountIdsPerRow() {
    if (this.bulkSleeveSimpleAccount.length > 0)
      this.rowData.forEach(node => {
        node.sleeveSimpleAccountIds = this.bulkSleeveSimpleAccount
          .filter(
            sleeveSimpleId =>
              sleeveSimpleId.id === Number(node.externalAccountId),
          )
          .map(data => data.sleeveSimple)[0]
      })
    else
      this.rowData.forEach(
        node => (node.sleeveSimpleAccountIds = this.sleeveSimpleAccount),
      )
  }

  async loadExtTransactionDetailBulk(transInput: number[]) {
    const sessionAccountId = await this.store
      .select(selectFirstLoadedAccountId)
      .pipe(take(1))
      .toPromise()
    const transactionDetail$: Observable<ExternalUnmatchedTransaction[]> =
      this.bulkTransactionService.loadExtTransactionDetailBulk(
        transInput,
        sessionAccountId,
      )
    const accountInfo$ = this.store
      .select(selectFirstFocusedAccountInfo)
      .pipe(take(1))
    return Promise.all([
      transactionDetail$.toPromise(),
      accountInfo$.toPromise(),
    ]).then(async ([extTransactionDetail, accountInfo]) => {
      if (this.transInput) {
        // Create a Map with the transaction details keyed by their id for faster lookup
        const transactionDetailById = new Map(
          extTransactionDetail.map(td => [td.externalTransactionId, td]),
        )
        // Sort the transaction details based on the order of transInput
        this.bulkExternalTransactionDetail = transInput
          .map(id => transactionDetailById.get(id))
          .filter((td): td is ExternalUnmatchedTransaction => Boolean(td))
        await this.setSessionAccounInfo(accountInfo)
      }
    })
  }

  /** @internal */
  async loadDataEdit(accounts: Set<number>) {
    const basisObservable = await this.bulkTransactionService
      .loadBasisForAccounts(Array.from(accounts))
      .toPromise()
    const collateralObservable = await this.bulkTransactionService
      .isCollateralBroker(Array.from(accounts))
      .toPromise()
    return Promise.all([basisObservable, collateralObservable]).then(
      ([basisMap, isCollateralMap]) => {
        this.basisMap = basisMap
        this.isCollateralMap = isCollateralMap
      },
    )
  }

  generateDataEntryFromPositionTransfer(
    positionData: BulkTransferPositionData,
    index: number,
  ): BulkSecurityTransferRow {
    return {
      rowId: (index + 1).toString(),
      accountId: positionData.accountId.toString(),
      entryDate: positionData.sessionReconDate,
      tranType: 'TRNO',
      units: positionData.units.toString(),
      assetId: positionData.assetId,
      transferPrice: '',
      inventoryType: positionData.inventoryType ?? 'LONG',
      collateralBroker: positionData.collateralBroker ?? '',
      validityStatus: 'intermediate',
      errorMessageMap: new Map(),
      mvtBasisSelection: [],
      mvtBasisSelectionText: TransactionConstants.MIN_FIELD_MESSAGE,
    }
  }

  generateDataEntryFromTransferEdit(
    cwTransaction: BulkSecurityTransferTransaction,
    index: number,
  ): BulkSecurityTransferRow {
    let mvtBasisSelection: MvtBasisSelection[] = []
    let cusip = undefined
    const securityDetails = this.securityMasterData.get(
      cwTransaction.securityId + '',
    )
    if (!isBulkCopyModal(this.transInput)) {
      mvtBasisSelection = this.buildMvDataForTransaction(
        this.transInput?.cwTranId
          ? cwTransaction.transactionId ?? ''
          : cwTransaction.legacyOrigTranId?.toString() ?? '',
        cwTransaction.accountId,
      )
      cusip = securityDetails?.cusip
    } else {
      cusip = cwTransaction.cusip
    }

    const factor = cwTransaction.factor ?? 1.0
    const transferPrice = cwTransaction.transferPrice

    const inventoryType = cwTransaction.inventoryType ?? 'LONG'

    const clientLotId =
      this.clientLotIdMapBulkPanel.get(cwTransaction.legacyOrigTranId + '') ??
      ''
    const securityTransferOut = {
      legacyOrigTranId: cwTransaction.legacyOrigTranId ?? -1,
      transactionId: cwTransaction.transactionId ?? '',
      legacyTranId: cwTransaction.legacyTranId ?? -1,
      rowId: (index + 1).toString(),
      accountId: cwTransaction.accountId.toString(),
      tranType: getTransactionTypeCodeFromKey(cwTransaction.tranType),
      units: cwTransaction.units
        ? Math.abs(cwTransaction.units).toString()
        : '',
      entryDate: cwTransaction.entryDate,
      assetId: cusip ?? '',
      securityId: cwTransaction.securityId,
      factor: factor,
      validityStatus: 'intermediate',
      errorMessageMap: new Map(),
      transferPrice: transferPrice?.toString() ?? '',

      inventoryType: inventoryType,
      collateralBroker: cwTransaction.dataMap?.collateralBroker ?? '',
      mvtBasisSelection: mvtBasisSelection,
      mvtBasisSelectionText: !isBulkCopyModal(this.transInput)
        ? this.getMarketValueTextFromMvtSelected(mvtBasisSelection)
        : TransactionConstants.MIN_FIELD_MESSAGE,
      clientLotId: cwTransaction.clientLotId ?? clientLotId,
      orderId: cwTransaction.orderId?.toString() ?? '',
      transactionBasis: cwTransaction.transactionBasis,
    } as BulkSecurityTransferRow
    if (cwTransaction.orderId) {
      securityTransferOut.orderId = cwTransaction.orderId
    }
    return securityTransferOut
  }

  buildMvDataForTransaction(
    tranId: string,
    accountId: number,
  ): MvtBasisSelection[] {
    const mvtBasisSelection: MvtBasisSelection[] = []
    const basisForAccount = this.basisMap[accountId]
    const mvtBasisIdForTransactionMap = new Map(
      Object.entries(this.mvtMapByTransaction[tranId]),
    )
    basisForAccount.forEach(basis => {
      const isMarketValue = mvtBasisIdForTransactionMap.get(
        basis.basisId.toString(),
      )
      mvtBasisSelection.push({
        basis: basis,
        accounts: [accountId],
        bookValue: !isMarketValue,
        marketValue: Boolean(isMarketValue),
      })
    })
    return mvtBasisSelection
  }

  generateDataEntryFromExternalTransfer(
    extTransaction: ExternalTransaction,
    index: number,
  ): BulkSecurityTransferRow {
    const factor = extTransaction.factor ?? 1.0
    const transferPrice = extTransaction.price
    let accountId = extTransaction.accountId.toString()
    const inventoryType = extTransaction.inventoryType ?? 'LONG'

    if ('CUSTODY' === extTransaction.dataSourceType) {
      if (this.hideAccountForSleeveAggregate(extTransaction.accountId)) {
        accountId = ''
      }
    }
    const externalTransferOut = {
      rowId: (index + 1).toString(),
      accountId: accountId,
      tranType: extTransaction.tranTypeCode
        ? getTransactionTypeCodeFromKey(
            extTransaction.tranTypeCode as TransactionTypeKey,
          )
        : '',
      units: extTransaction.units
        ? Math.abs(extTransaction.units).toString()
        : '',
      entryDate: extTransaction.tradeDate ?? '',
      assetId: extTransaction?.secIdentifier ?? '',
      securityId: extTransaction?.securityId,
      factor: factor,
      validityStatus: 'intermediate',
      errorMessageMap: new Map(),
      transferPrice: transferPrice?.toString() ?? '',
      inventoryType: inventoryType,
      collateralBroker:
        extTransaction.collateralBroker ??
        extTransaction.prometheusJson.aggInfo?.collateralBroker ??
        '',
      mvtBasisSelection: [],
      mvtBasisSelectionText: TransactionConstants.MIN_FIELD_MESSAGE,
      executionId: extTransaction.executionId,
      externalTranId: extTransaction.extTranId,
      externalAccountId: extTransaction.accountId,
      externalCusip: extTransaction.secIdentifier,
      externalTranStatus: extTransaction.status,
      externalDataSourceType: extTransaction.dataSourceType,
      transactionTypeLabel: extTransaction.transactionTypeLabel,
    } as BulkSecurityTransferRow

    if (!extTransaction.tranTypeCode) {
      const tranTypeMap = new Map<string, string[]>()
      tranTypeMap.set('tranType', ['Valid Value Required'])
      this.missingTranRowList.add((index + 1).toString())
      this.disableValidate = true
      externalTransferOut.validityStatus = 'invalid'
      externalTransferOut.errorMessageMap = tranTypeMap
    }
    return externalTransferOut
  }

  createFreshRowData() {
    this.rowData = []
    this.rowData.push(this.generateEmptyDataEntry(true, 1))
    for (let i = 1; i <= 9; i++) {
      this.rowData.push(this.generateEmptyDataEntry(false, i + 1))
    }
  }

  generateEmptyDataEntry(
    activated: boolean,
    id: number,
  ): BulkSecurityTransferRow {
    const tranTypeMap = new Map<string, string[]>()
    tranTypeMap.set('tranType', ['Valid Value Required'])
    if (activated) {
      this.missingTranRowList.add(id.toString())
    }

    return {
      rowId: id.toString(),
      accountId: '',
      tranType: '',
      units: '',
      entryDate: '',
      assetId: '',
      validityStatus: activated ? 'invalid' : 'inactive',
      errorMessageMap: activated ? tranTypeMap : new Map(),
      transferPrice: '',
      factor: 0,
      inventoryType: '',
      collateralBroker: '',
      mvtBasisSelection: [],
      mvtBasisSelectionText: activated
        ? TransactionConstants.MIN_FIELD_MESSAGE
        : '',
      transferSpecificLotId: '',
    }
  }

  loadBasis() {
    if (
      !isBulkCreateModal(this.transInput) &&
      !isBulkEditModal(this.transInput)
    ) {
      this.gridApi?.api?.forEachNode(async rowNode => {
        if (this.shouldOpenMvtPicker(rowNode.data)) {
          await this.initializeMvtBasis(rowNode, true)
        }
      })
    }
  }

  ngOnDestroy(): void {
    this.pasteService.destroy()
    this.copyService.destroy()
    this.destroySubject$.next()
    this.destroySubject$.complete()

    this.document.removeEventListener('pasteDone', this.onPasteEndBinding)
    this.document.removeEventListener(
      'deleteSuccessful',
      this.adjustTransactionsPostDeleteBinding,
    )
  }

  async onGridReady(event: GridReadyEvent) {
    this.gridApi = event
    this.pasteService.registerGrid(event.api, event.columnApi)
    this.copyService.registerGrid(event.api)
    this.bulkDeleteService.registerGrid(event.api)
    if (this.gridOptions.columnDefs)
      this.gridApi?.api.setColumnDefs(this.gridOptions.columnDefs)
    await this.savedViewService.manageSavedView(
      event,
      'Bulk-security-transfer-out-workspace-key',
      this.destroySubject$,
    )
    this.gridApi.api.setSortModel(null)
    this.loadBasis()
  }

  private createGridOptions(): GridOptions {
    return {
      ...heliosSizingAdjustments,
      onGridReady: event => this.onGridReady(event),
      getRowNodeId: ({ rowId }: BulkSecurityTransferRow) => rowId,
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        sortable: false,
      },
      sideBar: false,
      rowSelection: 'multiple',
      onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
        const element = event.event?.target as HTMLElement
        if (element.className == 'ag-fill-handle') {
          fillHandleDoubleClickHandler(event.api)
        } else {
          event.api.startEditingCell({
            rowIndex: this.currentRowIndex,
            colKey: event.column.getColId(),
          })
        }
      },

      onCellKeyDown: (event: CellKeyDownEvent) => {
        if (event.api.getEditingCells().length == 0) {
          emptySelectedCellsOnValidKeyPress(
            (event.event as KeyboardEvent).key,
            event.api,
          )
        }
      },
      suppressMultiRangeSelection: false,
      enableCellChangeFlash: true,
      enableRangeSelection: true,
      enableFillHandle: true,
      getContextMenuItems: () => this.getContextMenuItems(),
      columnDefs: this.colDefs,
      tooltipShowDelay: 0,
      onSortChanged: (params: SortChangedEvent) => onSortChange(params),
      onCellValueChanged: (event: CellValueChangedEvent) => {
        this.onColumnValueChanged(event)
      },
      onCellFocused: (event: CellFocusedEvent) => {
        this.handleCellFocus(event)
      },
    }
  }

  handleCellFocus(event: CellFocusedEvent) {
    this.currentRowIndex = event.rowIndex ?? 0
  }

  /**
   * The right click magic
   *
   * @internal
   */
  getContextMenuItems() {
    return [
      {
        name: 'Remove Row(s)',
        tooltip: 'Remove selected row(s)',
        action: () => {
          this.bulkDeleteService.deleteHandler()
        },
      },
      'export',
    ]
  }

  async onPasteEnd(evt: Event) {
    const pasteDetails = (evt as CustomEvent).detail
    this.pastePostProcessingInProgress = true
    this.disableValidate = true
    this.lastPastedRowIds = pasteDetails.pastedRowData.map(
      (rowData: { rowId: BulkSecurityTransferRow }) => rowData.rowId,
    )
    this.lastColumnIdsPasted = pasteDetails.colPastedSet

    this.loadingDialog = true
    this.pastePostProcessingInProgress = false
    this.allDataValidated = false
    this.applyOnChangeForPastedRowNodes()
    if (this.missingTranRowList.size == 0) this.disableValidate = false
    this.gridApi?.api.forEachNode(node => {
      if (
        node.data.validityStatus != 'inactive' &&
        node.data.inventoryType == ''
      )
        node.data.inventoryType = 'LONG'
    })

    this.loadingDialog = false
  }
  applyOnChangeForPastedRowNodes() {
    this.lastPastedRowIds.forEach(rowId => {
      const displayedNode = this.gridApi?.api.getRowNode(rowId)
      if (displayedNode) {
        if (displayedNode.data.mvtBasisSelection === '') {
          displayedNode.data.mvtBasisSelection = []
        }
        if (this.shouldOpenMvtPicker(displayedNode.data)) {
          this.initializeMvtBasis(
            displayedNode,
            !this.lastColumnIdsPasted.has('mvtBasisSelectionText'),
          )
        } else {
          displayedNode.setDataValue(
            'mvtBasisSelectionText',
            TransactionConstants.MIN_FIELD_MESSAGE,
          )
          displayedNode.setDataValue('mvtBasisSelection', [])
        }
        this.applyOnChangeForPastedRowNode(displayedNode)
      }
    })
  }

  applyOnChangeForPastedRowNode(displayedNode: RowNode) {
    const allColumns = this.gridApi?.columnApi.getAllColumns()
    allColumns
      ?.filter(column =>
        this.lastColumnIdsPasted.has(column.getColDef().field ?? ''),
      )
      .forEach(currentColumn => {
        const colId = currentColumn.getColDef().field
        this.onColumnValueChanged(
          {
            column: { getColId: () => colId },
            node: displayedNode,
            data: displayedNode.data,
            newValue: displayedNode?.data[colId ?? ''],
            oldValue: '',
          } as unknown as CellValueChangedEvent,
          this.lastColumnIdsPasted,
        )
      })
  }

  async onColumnValueChanged(
    event: CellValueChangedEvent,
    columnPastedIds?: Set<string>,
  ) {
    const rowNode = event.node
    const colId = event.column.getColId()
    if (
      rowNode.data[colId] === event.oldValue ||
      this.deletePostProcessingInProgress ||
      this.pastePostProcessingInProgress
    ) {
      return
    }
    if (
      rowNode?.data.validityStatus == 'inactive' &&
      (this.transInput?.modalMode == BulkTransactionModalMode.CreateCWTran ||
        this.transInput?.modalMode == BulkTransactionModalMode.CopyCWTran) &&
      colId != 'validityStatus' &&
      colId != 'errorMessageMap'
    ) {
      if (colId != 'tranType') {
        const tranTypeMap = new Map<string, string[]>()
        tranTypeMap.set('tranType', ['Valid Value Required'])
        rowNode.data.validityStatus = 'invalid'
        rowNode.data.errorMessageMap = tranTypeMap
        this.disableValidate = true
      }

      if (
        Number(rowNode?.rowIndex) + 1 ==
        this.gridApi?.api.getDisplayedRowCount()
      ) {
        const rows = [
          this.generateEmptyDataEntry(false, Number(rowNode.data.rowId) + 1),
        ]
        this.gridApi?.api.applyTransaction({ add: rows })
      }
    }
    if (
      colId != 'validityStatus' &&
      colId != 'errorMessageMap' &&
      rowNode.data.validityStatus != 'intermediate'
    ) {
      rowNode.data.validityStatus = 'intermediate'
      this.allDataValidated = false
    }

    switch (colId) {
      case 'entryDate':
      case 'accountId':
        if (colId != 'entryDate') this.handleAccountChange(event)
        if (!columnPastedIds) {
          this.handleMvtBasis(rowNode)
        }
        break
      case 'tranType':
        this.missingTranRowList.delete(event.node.data.rowId)
        if (this.missingTranRowList.size == 0) this.disableValidate = false
        this.handleMvtBasis(rowNode)
        break
      case 'validityStatus':
        this.onValidityStatusChanged(event)
        break
      case 'mvtBasisSelectionText':
        this.handleMvtSelectedChanged(event.node)
        break
    }
  }

  handleMvtBasis(rowNode: RowNode) {
    if (this.shouldOpenMvtPicker(rowNode.data)) {
      this.initializeMvtBasis(rowNode, true)
    } else {
      rowNode.setDataValue(
        'mvtBasisSelectionText',
        TransactionConstants.MIN_FIELD_MESSAGE,
      )
      rowNode.setDataValue('mvtBasisSelection', [])
    }
  }

  handleAccountChange(event: CellValueChangedEvent) {
    if (this.bulkSleeveSimpleAccount.length > 0) {
      this.sleeveSimpleAccount = this.bulkSleeveSimpleAccount
        .filter(data => data.id === Number(event.data['externalAccountId']))
        .map(data => data.sleeveSimple)[0]
    }
    if (this.sleeveSimpleAccount && this.sleeveSimpleAccount.length > 0) {
      const sAccount = this.sleeveSimpleAccount?.filter(
        data => data.displayLabel == event.data['accountId'],
      )[0]?.id
      if (sAccount) {
        event.data['sleeveAccountId'] = sAccount
      } else {
        event.node.setDataValue('accountId', '')
      }
    }
  }

  createColumnDefs(): ColDef[] {
    const colDefs: ColDef[] = [
      {
        field: securityField('validityStatus'),
        headerName: '',
        valueGetter: () => '',
        cellClass: (params: { data: { validityStatus: string } }) =>
          deduceValidityStatusCellClass(params.data.validityStatus),
        tooltipField: 'errorMessageMap',
        tooltipComponentParams: {
          showTooltip: params =>
            params?.data.errorMessageMap &&
            params.data.errorMessageMap.size > 0,
        } as NfTooltipParams,
        width: heliosDefaultColWidth.ValidityStatusCol,
        tooltipComponent: ErrorTooltipComponent,
        comparator: customSortComparatorBulkValidatorColumn,
      },
      {
        field: securityField('rowNumber'),
        headerName: '#',
        valueGetter: 'node.rowIndex + 1',
        width: heliosDefaultColWidth.SerialNumberCol,
        sortable: false,
      },
      {
        field: securityField('errorMessageMap'),
        hide: true,
      },
      {
        field: securityField('accountId'),
        headerName: 'Account ID',
        width: 100,
        type: getInlineEditColumnType(),
        cellEditorSelector: (x: EditableCallbackParams) =>
          getAccountCellParams(
            x,
            !!this.transInput?.externalTransaction,
            this.sessionAccountInfo?.accountType,
          ),
        cellStyle: (params: CellClassParams) => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        editable: (x: EditableCallbackParams) => this.isAccountEditable(x),
        valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
      },
      {
        field: securityField('tranType'),
        headerName: 'Tran Type',
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,

        width: 100,
        cellStyle: (params: CellClassParams) => {
          return this.deduceTranTypeCellStyle(params)
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.deduceTranTypeToolTip(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        cellEditorParams: {
          validators: [Validators.required],
          dataType: InlineEditDataType.Select,
          options: {
            items: this.transTypesIn,
          },
        },
        editable: () => !isBulkEditModal(this.transInput),
      },
      {
        field: securityField('assetId'),
        width: 120,
        type: getInlineEditColumnType(),
        headerName: 'Asset ID',
        cellStyle: (params: CellClassParams) => {
          return this.getCellStyle(params)
        },
        cellEditorParams: {
          dataType: InlineEditDataType.String,
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        editable: () => !isBulkEditModal(this.transInput),
        valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
      },

      {
        field: securityField('entryDate'),
        headerName: 'Transfer Date',
        width: 100,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.Date,
        },
        cellStyle: (params: CellClassParams) => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: securityField('units'),
        headerName: 'Orig Units',
        width: 100,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.Number,
        },
        cellStyle: (params: CellClassParams) => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        comparator: customSortComparatorBulkNumeric,
        valueGetter: params => {
          params.data.units = params.data.units
            ? Math.abs(params.data.units)
            : ''
          return params.data.units
        },
      },

      {
        field: securityField('transferPrice'),
        headerName: 'Transfer Price',
        width: 100,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.Number,
          disablePopover: true,
        },
        cellStyle: (params: CellClassParams) => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        comparator: customSortComparatorBulkNumeric,
        valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
      },

      {
        field: securityField('inventoryType'),
        headerName: 'Inventory Type',
        width: 120,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.Select,
          options: {
            items: inventories,
            bindValue: 'name',
            bindLabel: 'name',
          },
        },
        cellStyle: (params: CellClassParams) => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        columnGroupShow: 'open',
        valueGetter: params => {
          if (params.data.inventoryType == '') {
            params.data.inventoryType =
              params.data.validityStatus != 'inactive' ? 'LONG' : ''
          }

          return params.data.inventoryType
        },
      },

      {
        field: securityField('collateralBroker'),
        headerName: 'Collateral Broker',
        width: 100,
        type: getInlineEditColumnType(),
        cellEditorParams: {
          dataType: InlineEditDataType.String,
          disablePopover: true,
        },
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellStyle: (params: CellClassParams) => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        columnGroupShow: 'open',
        editable: (params: EditableCallbackParams) =>
          this.isCollateralEditable(params),
      },

      {
        field: securityField('transferSpecificLotId'),
        headerName: 'Transfer Specific Lot ID',
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        width: 115,
        comparator: customSortComparatorBulkNumeric,
        cellEditorParams: {
          dataType: InlineEditDataType.Number,
          disablePopover: true,
        },
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        editable: () => !isBulkEditModal(this.transInput),
      },

      {
        field: securityField('mvtBasisSelectionText'),
        editable: true,
        sortable: true,
        headerName: 'Market Value Transfer',
        tooltipComponentParams: {
          showTooltip: params => !this.shouldOpenMvtPicker(params?.data),
        } as NfTooltipParams,
        cellRenderer: undefined,
        cellRendererSelector: (params: ICellRendererParams) => {
          return this.getPopUpRenderer(params)
        },
        width: 225,
      },
      {
        field: securityField('mvtBasisSelection'),
        hide: true,
      },
      {
        field: securityField('balanceUnits'),
        hide: true,
      },
      {
        field: securityField('transactionBasis'),
        hide: true,
      },
      {
        field: securityField('transactionTypeLabel'),
        hide: true,
      },
    ]
    return colDefs
  }

  getPopUpRenderer(params: ICellRendererParams) {
    return {
      component: NgGridComponents.modalLinkRenderer,
      params: {
        modalComponent: BvMvBasisComponent,
        modalOptions: {
          size: 'md',
          windowClass: 'modal-window-click-through',
        } as NgbModalOptions,
        beforeOpen: () => this.shouldOpenMvtPicker(params.data),
        modalComplete: (mvtBasisSelectionUpdated: MvtBasisSelection[]) => {
          if (mvtBasisSelectionUpdated.length > 0)
            this.updateBasis(params.node, mvtBasisSelectionUpdated)
        },
      } as ModalLinkRendererParams,
    }
  }

  updateBasis(node: RowNode, mvtBasisSelectionUpdated: MvtBasisSelection[]) {
    node.setDataValue('mvtBasisSelection', mvtBasisSelectionUpdated)
    node.setDataValue(
      'mvtBasisSelectionText',
      this.getMarketValueTextFromMvtSelected(mvtBasisSelectionUpdated),
    )
  }

  isCollateralEditable(params: EditableCallbackParams) {
    if (
      this.isCollateralMap?.[params.data.accountId] ||
      !isBulkEditModal(this.transInput)
    ) {
      return true
    }
    return false
  }

  disableSaveValidRowsButton() {
    let disable = true

    this.gridApi?.api.forEachNode(node => {
      if (
        node.data.validityStatus == 'valid' ||
        node.data.validityStatus == 'warning'
      ) {
        disable = false // If at least one node has validity 'valid' or 'warning', set disable to false
        return // Exit the loop early if validity is 'valid'
      }
    })

    // Set disableSaveValidRows based on the value of disable
    this.disableSaveValidRows = disable
  }

  // Flags active rows with no account as invalid client-side so the user gets an
  // inline "Account is required" error on the cell instead of a silently disabled
  // Validate button. Returns true if any row was missing an account, in which case
  // validate() short-circuits and never sends accountId 0 to the backend.
  markMissingAccountRows(): boolean {
    const anyMissing = markRowsMissingAccount(
      this.gridApi?.api,
      'accountId',
      this.deduceImpactedField('accountId'),
      this.cellLevelErrorMessageMap,
    )
    if (anyMissing) {
      this.isDataValid = false
    }
    return anyMissing
  }

  async deriveAndValidate() {
    if (this.markMissingAccountRows()) {
      return
    }
    this.getMarketValueTextsFromMvtsSelected()
    this.errorDetails = ''
    await Promise.all([
      this.populateTransferDetails(),
      this.populateDerivedFields(),
    ])
    await this.validate()
  }

  async populateTransferDetails() {
    const origUnitsPayload: OrigUnitsRequest[] = []
    this.gridApi?.api?.forEachNode(rowNode => {
      const { collateralBroker, inventoryType, transferSpecificLotId } =
        rowNode.data
      if (
        rowNode.data.validityStatus != 'inactive' &&
        rowNode.data.assetId != '' &&
        rowNode.data.entryDate != '' &&
        rowNode.data.accountId != ''
      ) {
        origUnitsPayload.push({
          cusip: rowNode.data.assetId,
          entryDate: rowNode.data.entryDate,
          accountId: Number(rowNode.data.accountId),
          rowId: Number(rowNode.data.rowId),
          ...(inventoryType && { inventoryType }),
          ...(collateralBroker && { collateralBroker }),
          ...(transferSpecificLotId && {
            lotEntryTranId: Number(transferSpecificLotId),
          }),
        })
      }
    })
    this.loadingText = 'Loading Units'
    this.isValidating = true
    try {
      const res = await this.bulkTransactionService
        .getTransferDetails(origUnitsPayload)
        .toPromise()
      origUnitsPayload.forEach(rowNode => {
        const balanceUnits = res.tranDetails[rowNode.rowId]?.balanceUnits
        if (balanceUnits !== undefined) {
          this.gridApi?.api.forEachNode(node => {
            if (Number(node.data.rowId) === Number(rowNode.rowId)) {
              node.setDataValue('balanceUnits', balanceUnits)
              if (!node.data.units) node.setDataValue('units', balanceUnits)
            }
          })
        }
      })
      this.isValidating = false
    } catch (error) {
      this.errorHeader = 'Error Fetching Units'
      this.errorDetails = error.error
      this.isValidating = false
    }
  }

  getMarketValueTextsFromMvtsSelected() {
    this.gridApi?.api?.forEachNode(rowNode => {
      if (
        rowNode.data.validityStatus != 'inactive' &&
        (rowNode.data.mvtBasisSelectionText ==
          TransactionConstants.MIN_FIELD_MESSAGE ||
          rowNode.data.mvtBasisSelectionText == '')
      ) {
        rowNode.setDataValue(
          'mvtBasisSelectionText',
          this.getMarketValueTextFromMvtSelected(
            rowNode.data.mvtBasisSelection,
          ),
        )
      }
    })
  }

  async populateDerivedFields() {
    this.isDeriving = true
    this.loadingText = 'Fetching Derived Fields'
    this.pricingRequestToRowIds.clear()
    const derivedRequest = this.getGridTransactionsDerived()
    try {
      if (derivedRequest.length > 0) {
        const pricingInfo = await this.bulkTransactionService
          .loadBulkPricingInformation(derivedRequest)
          .toPromise()

        const pricingData = new Map<string, PricingResponse>(
          Object.entries(pricingInfo),
        )
        pricingData.forEach((value, key) => {
          const responseKeyToPricingRequest =
            convertBulkResponseKeyToPricingRequest(key)
          const impactedRowIds = this.pricingRequestToRowIds.get(
            JSON.stringify(responseKeyToPricingRequest),
          )
          impactedRowIds?.forEach(impactedRowId => {
            const impactedRowNode = this.gridApi?.api.getRowNode(
              impactedRowId ?? '',
            )
            if (impactedRowNode?.data.transferPrice == '') {
              impactedRowNode?.setDataValue('transferPrice', value.price ?? 0)
            }
          })

          this.cusipToSecurityMap.set(
            responseKeyToPricingRequest.cusip,
            value.securityId ?? 0,
          )
        })
      }
      this.isDeriving = false
    } catch (error) {
      this.errorHeader = TransactionConstants.ERROR_FETCH_DERIVED_FIELDS
      this.errorDetails = error.error
      this.isDeriving = false
    }
  }

  getGridTransactionsDerived(): PricingRequest[] {
    const pricingRequests: PricingRequest[] = []

    this.gridApi?.api?.forEachNode(rowNode => {
      const { collateralBroker } = rowNode.data
      const inventoryType =
        Object.keys(InventoryType)[
          Object.values(InventoryType).indexOf(
            rowNode.data.inventoryType as InventoryType,
          )
        ]
      if (
        rowNode.data.validityStatus != 'inactive' &&
        rowNode.data.assetId != '' &&
        rowNode.data.entryDate != '' &&
        rowNode.data.accountId != '' &&
        (!this.cusipToSecurityMap.get(rowNode.data.assetId) ||
          rowNode.data.transferPrice == '')
      ) {
        const pricingRequest: PricingRequest = {
          accountId: Number(rowNode.data.accountId),
          cusip: rowNode.data.assetId,
          entryDate: rowNode.data.entryDate,
          ...(inventoryType && { inventoryType }),
          ...(collateralBroker && { collateralBroker }),
        }

        const pricingRequestForRequestToRowIds = {
          accountId: pricingRequest.accountId,
          cusip: pricingRequest.cusip,
          entryDate: pricingRequest.entryDate,
        }
        const pricingRow = this.pricingRequestToRowIds.get(
          JSON.stringify(pricingRequestForRequestToRowIds),
        )

        if (pricingRow) {
          pricingRow.push(rowNode.data.rowId)
        } else {
          this.pricingRequestToRowIds.set(
            JSON.stringify(pricingRequestForRequestToRowIds),
            [rowNode.data.rowId],
          )
        }

        pricingRequests.push(pricingRequest)
      }
    })
    return pricingRequests
  }

  async validate() {
    this.isDataValid = true
    this.isCommonValidationError = false
    if (this.markMissingAccountRows()) {
      return
    }
    const bulkValidateView = this.createBulkValidationView()
    this.isValidating = true
    try {
      this.loadingText = TransactionConstants.VALIDATE_TRANSACTION
      const validationInfos = await this.bulkTransactionService
        .validateBulkTransaction(bulkValidateView, TranCategoryUrl.TRANSFER)
        .toPromise()
      this.handleBulkValidationResponse(validationInfos.validationMap)
      this.handleCommonValidations(validationInfos.commonValidations)
      this.allDataValidated = true
      this.disableSaveValidRowsButton()
      this.isValidating = false
    } catch (error) {
      this.errorHeader = TransactionConstants.VALIDATION_ERROR
      this.errorDetails = error.error
      this.isValidating = false
    }
  }

  handleCommonValidations(commonValidations: ValidationInfo[]) {
    this.genericErrors = []
    this.genericWarnings = []
    this.currentNoteError = null
    if (commonValidations.length == 0) {
      this.showConfirmButtonModal = false
    }
    commonValidations.forEach(validationInfo => {
      if (validationInfo.impactedFields.length == 0) {
        validationInfo.impactedFields = ['generic']
      }
      validationInfo.impactedFields.forEach(field => {
        if (field == 'transactionNote') {
          this.isDataValid = false
          this.isCommonValidationError = true
          const validatorKey = 'transactionNoteValidator'
          if (validationInfo.level == ErrorSeverity.ERROR) {
            this.note.markAsTouched()
            this.currentNoteError = {
              [validatorKey]: {
                message: validationInfo.message,
              },
            }
            this.note.setErrors(this.currentNoteError)
          } else if (validationInfo.level == ErrorSeverity.WARN) {
            this.noteWarning = {
              [validatorKey]: {
                message: validationInfo.message,
              },
            }
          }
        } else if (
          field == 'confirmButton' &&
          validationInfo.level == ErrorSeverity.WARN
        ) {
          this.showConfirmButtonModal = true
        } else {
          //generic case
          this.isDataValid = false
          this.isCommonValidationError = true
          if (validationInfo.level == ErrorSeverity.ERROR) {
            this.genericErrors.push(validationInfo.message)
          } else if (validationInfo.level == ErrorSeverity.WARN) {
            this.genericWarnings.push(validationInfo.message)
          }
        }
      })
    })
  }

  toolTipValueGetter(params: ITooltipParams): string {
    const impactedFieldColDef = params.colDef as ColDef
    const impactedFieldProcessed = this.deduceImpactedField(
      impactedFieldColDef.field ?? '',
    )
    const index = params.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    const currentValidationErrors = this.cellLevelErrorMessageMap.get(cellKey)
    const currentValidationWarnings =
      this.cellLevelWarningMessageMap.get(cellKey)
    if (currentValidationErrors) {
      return currentValidationErrors.join(' | ')
    } else if (currentValidationWarnings) {
      return currentValidationWarnings.join(' | ')
    } else {
      return params.value
    }
  }

  getCellStyle(params: CellClassParams): CellStyle {
    const impactedFieldProcessed = this.deduceImpactedField(
      params.colDef.field ?? '',
    )
    const index = params.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    if (this.cellLevelErrorMessageMap.has(cellKey)) {
      return { 'background-color': 'lightcoral' }
    } else if (this.cellLevelWarningMessageMap.has(cellKey)) {
      return { 'background-color': 'yellow' }
    } else {
      return { 'background-color': '#00000000' }
    }
  }
  deduceTranTypeCellStyle(params: CellClassParams) {
    if (
      params.data.tranType == '' &&
      params.data.validityStatus != 'inactive'
    ) {
      return { 'background-color': 'lightcoral' }
    } else {
      return this.getCellStyle(params)
    }
  }

  deduceTranTypeToolTip(params: ITooltipParams) {
    if (
      params.data.tranType == '' &&
      params.data.validityStatus != 'inactive'
    ) {
      return 'Valid Value Required'
    } else {
      return this.toolTipValueGetter(params)
    }
  }

  handleBulkValidationResponse(bulkValidationInfos: {
    [key: number]: ValidationInfo[]
  }) {
    // create errorMessages column per row and update tooltip with it

    this.gridApi?.api?.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive') {
        rowNode.data.validityStatus = 'valid'
        rowNode.data.errorMessageMap = new Map()
      }
    })

    this.note.setErrors(null)
    this.cellLevelErrorMessageMap.clear()
    this.cellLevelWarningMessageMap.clear()
    const bulkValidationInfo = new Map<string, ValidationInfo[]>(
      Object.entries(bulkValidationInfos),
    )
    bulkValidationInfo.forEach((rowValidationInfo: ValidationInfo[], index) => {
      // create a map of key index,field and keep all errors(string or string[](if erros ) and warnings (if onlky warnings)
      // in cell class rule make it yellow if warning validity status
      // in tooltip use above map to put all erros in tooltip
      const currentRowNode = this.gridApi?.api.getRowNode(index)
      const errorMessages = new Map<string, string[]>()
      const warningMessages = new Map<string, string[]>()

      rowValidationInfo.forEach((validationInfo: ValidationInfo) => {
        const isError = validationInfo.level === ErrorSeverity.ERROR
        const messageMap = isError
          ? this.cellLevelErrorMessageMap
          : this.cellLevelWarningMessageMap
        const messages = isError ? errorMessages : warningMessages
        validationInfo.impactedFields.forEach((impactedField: string) => {
          const impactedFieldProcessed = this.deduceImpactedField(impactedField)
          const currentKey = `${index}-${impactedFieldProcessed}` // More efficient key representation
          // message map
          const msgList = messageMap.get(currentKey) || []
          msgList.push(validationInfo.message)
          messageMap.set(currentKey, msgList)
          // messages
          const fieldMessages = messages.get(impactedFieldProcessed) || []
          fieldMessages.push(validationInfo.message)
          messages.set(impactedFieldProcessed, fieldMessages)
        })
      })
      if (errorMessages.size > 0 && currentRowNode) {
        this.isDataValid = false
        currentRowNode.data.validityStatus = 'invalid'
        currentRowNode.data.errorMessageMap = errorMessages
      }
      if (
        errorMessages.size == 0 &&
        warningMessages.size > 0 &&
        currentRowNode
      ) {
        currentRowNode.data.validityStatus = 'warning'
        currentRowNode.data.errorMessageMap = warningMessages
      }

      if (rowValidationInfo.length == 0 && currentRowNode) {
        currentRowNode.data.validityStatus = 'valid'
        currentRowNode.data.errorMessageMap = new Map()
      }
    })
    this.gridApi?.api.redrawRows()
  }

  deduceImpactedField(impactedFieldFromValidationInfo: string): string {
    if (impactedFieldFromValidationInfo == 'securityId') {
      return 'assetId'
    }
    if (impactedFieldFromValidationInfo == 'orgUnits') {
      return 'units'
    }
    if (impactedFieldFromValidationInfo == 'accountId') {
      return 'account'
    }
    if (impactedFieldFromValidationInfo == 'specLotMatchDetails') {
      return 'transferSpecificLotId'
    } else {
      return impactedFieldFromValidationInfo
    }
  }

  createBulkValidationView(): BulkSecurityTransferValidationView {
    const view: BulkSecurityTransferValidationView = {
      transaction: this.getGridTransactions(true),
      requestMode: bulkRequestMode(this.transInput?.modalMode),
      reconDate: this.sessionReconDate ?? this.selectedReconDate ?? new Date(),
      tranCategory: TranCategory.TRANSFER,
    }
    if (this.note.value !== '') view.transactionNote = this.note.value
    return view
  }

  getGridOnlyValidTransactions(isValidate: boolean) {
    const transactions: TransactionRequestWrapper[] = []
    this.gridApi?.api?.forEachNode(rowNode => {
      if (
        rowNode.data.validityStatus == 'valid' ||
        rowNode.data.validityStatus == 'warning'
      )
        transactions.push(
          this.prepareBulkTransactionData(isValidate, rowNode.data),
        )
    })
    return transactions
  }

  getGridTransactions(isValidate: boolean) {
    const transactions: TransactionRequestWrapper[] = []
    this.gridApi?.api?.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive')
        transactions.push(
          this.prepareBulkTransactionData(isValidate, rowNode.data),
        )
    })
    return transactions
  }
  prepareBulkTransactionData(
    isValidate: boolean,
    rowData: BulkSecurityTransferRow,
  ): TransactionRequestWrapper {
    const units = this.deduceSignedUnits(
      Number(rowData.units),
      rowData.inventoryType,
    )
    let currentOriginalTransaction = undefined
    if (!isBulkCopyModal(this.transInput)) {
      currentOriginalTransaction = this.originalTransactions.find(
        origTran => origTran.legacyOrigTranId == rowData.legacyOrigTranId,
      )
    }

    let createdDate = new Date().toISOString()
    if (currentOriginalTransaction?.createdDate) {
      createdDate = new Date(
        currentOriginalTransaction?.createdDate,
      ).toISOString()
    }
    const transTypeCode = Object.keys(TransactionTypeEnum).filter(
      x =>
        TransactionTypeEnum[x as TransactionTypeKey] ==
        getTransactionTypeNameFromCode(rowData.tranType),
    )[0] as TransactionTypeKey

    const originalSettleDate = rowData[
      'GAAPOrigSettleDate' as keyof typeof rowData
    ] as string
    let realSettle = rowData.entryDate
    if (originalSettleDate) {
      realSettle =
        compareAsc(
          parseISO(rowData.entryDate),
          parseISO(originalSettleDate),
        ) === -1
          ? originalSettleDate
          : rowData.entryDate
    }
    let securityId = rowData.securityId
    if (!isBulkEditModal(this.transInput)) {
      securityId = this.cusipToSecurityMap.get(rowData.assetId)
    }

    let account = rowData.accountId
    if (rowData.sleeveAccountId) {
      account = rowData.sleeveAccountId?.toString()
    }

    const transactionData: BulkSecurityTransferTransaction = {
      tranType: transTypeCode,
      userId: this.userId ?? 0,
      accountId: Number(account),
      entryDate: rowData.entryDate,
      securityId: securityId,
      postDate: realSettle,
      settleDate: realSettle,
      counterParty: getDefaultUnknownCounterParty(),
      counterPartyId: getDefaultUnknownCounterParty().id,
      typeLabel: rowData.transactionTypeLabel,
    }
    transactionData.dataMap = {}

    if (rowData.collateralBroker) {
      transactionData.dataMap.collateralBroker = rowData.collateralBroker
    }
    if (
      rowData.transactionId &&
      this.initialClientLotIdMap.get(rowData.transactionId)
    ) {
      transactionData.dataMap.clientLotId = this.initialClientLotIdMap.get(
        rowData.transactionId,
      )
    }

    if (rowData.transferPrice !== '') {
      transactionData.transferPrice = Number(rowData.transferPrice)
    }
    if (rowData.units !== '') {
      transactionData.units = units
      if (isValidate) {
        transactionData.currUnits = transactionData.units
      } else {
        transactionData.curUnits = transactionData.units
      }
    }

    if (rowData.inventoryType !== '') {
      transactionData.inventoryType = rowData.inventoryType as InventoryType
    }

    if (
      (this.sessionReconDate &&
        !isAfter(new Date(realSettle), new Date(this.sessionReconDate))) ||
      (!this.sessionReconDate &&
        this.selectedReconDate &&
        !isAfter(new Date(realSettle), new Date(this.selectedReconDate)))
    ) {
      transactionData.clearedDate = isValidate
        ? new Date().toISOString().split('Z')[0]
        : new Date().toISOString()
    }

    if (!isValidate) {
      transactionData.revisionId = currentOriginalTransaction?.revisionId
        ? currentOriginalTransaction.revisionId + 1
        : 1
      transactionData.sourceId = TransactionConstants.HELIOS_SOURCE_ID
      transactionData.createdDate = createdDate
      transactionData.modifiedDate = new Date().toISOString()
      transactionData.active = true
    }

    if (currentOriginalTransaction) {
      transactionData.transactionId = currentOriginalTransaction.transactionId
      transactionData.legacyOrigTranId =
        currentOriginalTransaction.legacyOrigTranId
      transactionData.legacyTranId = currentOriginalTransaction.legacyTranId
      transactionData.clearedDate = currentOriginalTransaction?.clearedDate
    }

    const metaData: MetaDataTransaction = {
      rowId: Number(rowData.rowId),
      reconDate: this.sessionReconDate ?? this.selectedReconDate ?? new Date(),
      cusip: rowData.assetId,
      balanceUnits: rowData.balanceUnits,
      existingUnits:
        this.originalTransactions.find(
          origTran => origTran.legacyOrigTranId == rowData.legacyOrigTranId,
        )?.units ?? 0,
      reprocessDownstreamTransfers: this.reprocessTransfers.value,
      basis: rowData.transactionBasis,
    }
    if (rowData.transferSpecificLotId) {
      metaData.specLotMatchDetails = {
        lotEntryTranId: Number(rowData.transferSpecificLotId),
        units: units,
      }
    }

    if (isBulkExternalModal(this.transInput)) {
      metaData.externalAccountId = rowData.externalAccountId
      metaData.executionId = rowData.executionId
      metaData.externalTranId = rowData.externalTranId
      metaData.externalCusip = rowData.externalCusip
      metaData.externalTranStatus = rowData.externalTranStatus
      metaData.dataSourceType = rowData.externalDataSourceType
    }

    const selectedBasis = rowData.mvtBasisSelectionText?.split(',')
    const bases: MvtBasisSelection[] = []

    selectedBasis?.forEach(basis => {
      const foundBasis = rowData.mvtBasisSelection?.find(
        b => b.basis.prefix == basis,
      )
      if (foundBasis) bases.push(foundBasis)
    })
    metaData.mvtBases = Object.fromEntries(this.getSelectedMvtBases(bases))

    return { transactionData, metaData }
  }

  getSelectedMvtBases(
    mvtBasisSelection?: MvtBasisSelection[],
  ): Map<number, Basis> {
    const mvtBasesMap = new Map<number, Basis>()
    mvtBasisSelection?.forEach(mvtBase => {
      if (mvtBase.marketValue) {
        mvtBasesMap.set(mvtBase.basis.basisId, mvtBase.basis)
      }
    })
    return mvtBasesMap
  }

  validateAndSave(isSaveValidRows?: boolean) {
    if (this.showConfirmButtonModal) {
      const modalRef = this.modalService.open(
        BulkTransactionsSaveConfirmationModalComponent,
        { keyboard: false },
      )
      modalRef.result.then((confirmed: boolean) => {
        if (confirmed) {
          return isSaveValidRows ? this.saveValidRows() : this.save()
        } else return
      })
    } else if (isSaveValidRows) this.saveValidRows()
    else this.save()
  }

  async saveValidRows() {
    await this.save(true)
    const newRowData: BulkSecurityTransferRow[] = []
    this.gridApi?.api.forEachNode(node => {
      if (
        node.data.validityStatus != 'valid' &&
        node.data.validityStatus != 'warning'
      )
        newRowData.push(node.data)
    })

    this.gridApi?.api.setRowData(newRowData)
    this.disableSaveValidRowsButton()
  }

  async save(onlyValidRows?: boolean) {
    if (!this.isDataValid && !onlyValidRows) {
      return
    }
    this.formSubmitting = true
    this.loadingText = TransactionConstants.SAVE_TRANSACTION
    await this.blankSessionService.createBlankSessionIfNotInSession()
    const tranCommand: BulkTransactionRequest = {
      transaction: onlyValidRows
        ? this.getGridOnlyValidTransactions(false)
        : this.getGridTransactions(false),
      tranCategory: TranCategory.TRANSFER,
      requestMode: bulkRequestMode(this.transInput?.modalMode),
    }
    if (this.note.value !== '') {
      tranCommand.transactionNote = { newValue: this.note.value }
    }

    if (isBulkEditModal(this.transInput)) {
      tranCommand.notes =
        TransactionConstants.HELIOS_BULK_SECURITY_TRANSFER_SAVE_NOTE_CW_EDIT
      this.bulkTransactionService
        .editBulkTransaction(tranCommand, TranCategoryUrl.TRANSFER)
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(
          () => {
            this.toastService.success(
              'Bulk Transaction edit command successfully submitted.',
            )
            this.formSubmitting = false
            if (!onlyValidRows) this.closeModal(true, tranCommand.transaction)
          },
          error => {
            this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
            this.errorDetails = error.error
            this.formSubmitting = false
          },
        )
    } else if (isBulkExternalModal(this.transInput)) {
      tranCommand.notes =
        TransactionConstants.HELIOS_BULK_SECURITY_TRANSFER_SAVE_NOTE_EXTERNAL
      this.bulkTransactionService
        .createBulkTransaction(tranCommand, TranCategoryUrl.TRANSFER)
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(
          () => {
            this.toastService.success(
              'Bulk External Transaction command successfully submitted.',
            )
            this.formSubmitting = false
            if (!onlyValidRows) this.closeModal(true, tranCommand.transaction)
          },
          error => {
            this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
            this.errorDetails = error.error
            this.formSubmitting = false
          },
        )
    } else {
      tranCommand.notes =
        TransactionConstants.HELIOS_BULK_SECURITY_TRANSFER_SAVE_NOTE_CW_CREATE
      this.bulkTransactionService
        .createBulkTransaction(tranCommand, TranCategoryUrl.TRANSFER)
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(
          () => {
            this.toastService.success(
              'Bulk Transaction create command successfully submitted.',
            )
            this.formSubmitting = false
            if (!onlyValidRows) this.closeModal(true, tranCommand.transaction)
          },
          error => {
            this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
            this.errorDetails = error.error
            this.formSubmitting = false
          },
        )
    }
  }
  setNoteErrors() {
    if (this.currentNoteError != null) {
      this.note.setErrors(this.currentNoteError)
    }
  }

  private closeModal(
    success: boolean,
    transactionRequests: TransactionRequestWrapper[],
  ): void {
    this.activeModal.close({
      success,
      transactionRequests,
    } as BulkTransactionModalResponse)
  }

  async loadTransactionDetailBulk(cwTransId: string[], securityId: number[]) {
    const transactionDetail$: Observable<ClearwaterTransactionDetailResponse> =
      this.bulkTransactionService.loadTransactionDetailBulk(
        cwTransId,
        securityId,
        TranCategoryUrl.TRANSFER,
        true,
      )
    return Promise.all([transactionDetail$.toPromise()]).then(
      ([transactionDetail]) => {
        if (this.transInput) {
          const unorderedTransactionDetails =
            transactionDetail.transactionDataMap
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const orderedTransactionDetails: { [key: string]: any } = {}

          // Sort the transaction details based on the order of cwTransid
          for (const id of cwTransId) {
            if (unorderedTransactionDetails[id]) {
              orderedTransactionDetails[id] = unorderedTransactionDetails[id]
            }
          }

          this.clearwaterTransactionDetail = orderedTransactionDetails
          this.securityMasterData = new Map<string, SecurityMasterData>(
            Object.entries(transactionDetail.securityMasterDataMap ?? ''),
          )
          this.mvtMapByTransaction = transactionDetail.mvtDataMap ?? {}
        }
      },
    )
  }

  async loadAdditionalDetailBulk(
    legacyOrigIds: number[],
    legacyTranIds: number[],
    securityIds: number[],
    accountIds: number[],
  ) {
    const additionalDetails$: Observable<AdditionalDetailsSecurityTransfer> =
      this.bulkTransactionService.loadAdditionalDetailsTransferBulk(
        legacyOrigIds,
        legacyTranIds,
        securityIds,
        accountIds,
        true,
      )
    return Promise.all([additionalDetails$.toPromise()]).then(
      ([additionalDetails]) => {
        if (this.transInput) {
          this.securityMasterData = new Map<string, SecurityMasterData>(
            Object.entries(additionalDetails.securityMasterDataMap ?? ''),
          )
          this.mvtMapByTransaction = additionalDetails.mvtDataMap ?? {}
          this.clientLotIdMapBulkPanel = new Map<string, string>(
            Object.entries(additionalDetails.clientLotIdMap ?? ''),
          )
        }
      },
    )
  }

  disableGridFocus() {
    this.gridApi?.api.clearRangeSelection()
  }

  adjustTransactionsPostDelete() {
    this.deletePostProcessingInProgress = true
    this.missingTranRowList.clear()
    this.disableValidate = true
    this.gridApi?.api.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive') {
        if (rowNode.data.tranType == '') {
          this.missingTranRowList.add(rowNode.data.rowId.toString())
        }
      }
    })
    if (this.missingTranRowList.size == 0) this.disableValidate = false
    this.deletePostProcessingInProgress = false
  }

  async loadNonSessionAccountTypeInfo(
    uniqueAccountId: number[],
  ): Promise<void> {
    const accountInfoPromise = this.bulkTransactionService
      .fetchAccountTypes(uniqueAccountId)
      .toPromise()
    await Promise.all([accountInfoPromise]).then(async accountTypeObj => {
      if (!accountTypeObj) return
      this.sleeveSimpleAccount = Object.entries(accountTypeObj[0])
        .map(([key, value]) => {
          return {
            id: Number(key),
            type: value,
          }
        })
        .filter(data => data.type === 'sleeveAggregate')

      if (this.sleeveSimpleAccount.length > 0) {
        this.sleeveAccountIds = this.sleeveSimpleAccount.map(({ id }) => id)

        const subAccounts = await this.bulkTransactionService
          .fetchAllSubAccounts(this.sleeveAccountIds)
          .toPromise()

        if (!subAccounts) return

        this.bulkSleeveSimpleAccount = Object.entries(subAccounts).map(
          ([key, value]) => {
            return {
              id: Number(key),
              sleeveSimple: value,
            }
          },
        )
      }
    })
  }

  isAccountEditable(params: EditableCallbackParams): boolean {
    if (
      'sleeveAggregate' === this.sessionAccountInfo?.accountType ||
      this.transInput?.externalTransaction
    ) {
      if ('sleeveAggregate' === this.sessionAccountInfo?.accountType) {
        return this.isSessionAccountEditable()
      } else {
        return this.isNonSessionAccountsEditable(params)
      }
    } else if (!this.isEdit) {
      return true
    }
    return false
  }

  isSessionAccountEditable() {
    return true
  }

  isNonSessionAccountsEditable(params: EditableCallbackParams) {
    if (this.sleeveAccountIds?.includes(params.data['externalAccountId'])) {
      return true
    }
    return false
  }

  async setSessionAccounInfo(accountInfo?: AccountInfo) {
    if (accountInfo) {
      this.sessionAccountInfo = accountInfo
      if ('sleeveAggregate' === accountInfo.accountType) {
        const subAccounts = await this.bulkTransactionService
          .fetchSubAccounts(accountInfo.accountId)
          .toPromise()
        this.sleeveSimpleAccount = subAccounts
      }
    }
  }

  hideAccountForSleeveAggregate(accountId: number) {
    if (
      'sleeveAggregate' === this.sessionAccountInfo?.accountType &&
      this.sessionAccountInfo?.accountId == accountId
    ) {
      return true
    } else if (this.sleeveAccountIds?.includes(accountId)) {
      return true
    }
    return false
  }
  clearAll() {
    this.gridApi?.api.applyTransaction({
      remove: this.gridApi?.api.getRenderedNodes().map(rowNode => rowNode.data),
    })

    this.createFreshRowData()
    this.gridApi?.api.applyTransaction({ add: this.rowData })
    this.cellLevelErrorMessageMap.clear()
    this.cellLevelWarningMessageMap.clear()
    this.disableValidate = true
    this.errorDetails = ''
  }

  onValidityStatusChanged(event: CellValueChangedEvent) {
    if (
      event.node.data.tranType == '' &&
      event.node.data.validityStatus == 'invalid'
    ) {
      this.missingTranRowList.add(event.node.data.rowId)
      this.disableValidate = true
    }
  }

  showToolTip(params?: ITooltipParams) {
    const impactedFieldColDef = params?.colDef as ColDef
    const impactedFieldProcessed = this.deduceImpactedField(
      impactedFieldColDef.field ?? '',
    )
    const index = params?.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    const currentValidationErrors = this.cellLevelErrorMessageMap.get(cellKey)
    const currentValidationWarnings =
      this.cellLevelWarningMessageMap.get(cellKey)
    if (
      (currentValidationErrors && currentValidationErrors?.length > 0) ||
      (currentValidationWarnings && currentValidationWarnings?.length > 0) ||
      (params?.data.tranType == '' && params?.data.validityStatus != 'inactive')
    ) {
      return true
    }
    return false
  }

  shouldOpenMvtPicker(data: BulkSecurityTransferRow): boolean {
    return data.accountId != '' && data.entryDate != '' && data.tranType != ''
  }

  deduceSignedUnits(units: number, inventoryType: string): number {
    if (inventoryType == 'LONG') return -1 * Math.abs(units)
    else return units
  }

  async initializeMvtBasis(
    rowNode: RowNode,
    isMarketValueTextNotPasted: boolean,
  ) {
    const mvtData: MarketValueTransferModel = {
      category: TranCategory.TRANSFER,
      accountId: !Number.isNaN(Number(rowNode.data.accountId))
        ? Number(rowNode.data.accountId)
        : Number(rowNode.data.sleeveAccountId),
      tranType: getTransactionTypeKeyFromCode(rowNode.data.tranType),
      destAccountId: undefined,
      entryDate: rowNode.data.entryDate,
    }
    const mvtBasisExisting = this.mvtDataLoaded.get(JSON.stringify(mvtData))
    if (mvtBasisExisting) {
      this.setUpMvtBasis(rowNode, isMarketValueTextNotPasted, mvtBasisExisting)
    } else {
      this.loadingBasis = true
      this.loadingText = TransactionConstants.LOAD_BASIS
      const mvtBasis = await this.bulkTransactionService
        .loadMvtValues(mvtData)
        .toPromise()

      this.mvtDataLoaded.set(JSON.stringify(mvtData), mvtBasis)
      this.setUpMvtBasis(rowNode, isMarketValueTextNotPasted, mvtBasis)
      this.loadingBasis = false
      // err => {
      //   this.errorText = TransactionConstants.ERROR_LOAD_BASIS
      //   this.errorDetails = err.error
      // },
    }
  }
  setUpMvtBasis(
    rowNode: RowNode,
    isMarketValueTextNotPasted: boolean,
    mvtBasis: MvtBasis,
  ) {
    const allBasis = buildMvtBasisSelection(
      Number(rowNode.data.accountId),
      mvtBasis,
      true,
    )
    rowNode.setDataValue('mvtBasisSelection', allBasis)
    if (isMarketValueTextNotPasted)
      rowNode.setDataValue(
        'mvtBasisSelectionText',
        this.getMarketValueTextFromMvtSelected(allBasis),
      )
  }

  getMarketValueTextFromMvtSelected(
    mvtBasisSelection: MvtBasisSelection[],
  ): string {
    const marketValueBasis = mvtBasisSelection.filter(
      bases => bases.marketValue,
    )
    return marketValueBasis.length == 0
      ? TransactionConstants.NO_MARKET_VALUE_TRANSFER
      : marketValueBasis.map(bases => bases.basis.prefix).join()
  }
  handleMvtSelectedChanged(rowNode: RowNode) {
    if (!this.shouldOpenMvtPicker(rowNode.data)) {
      rowNode.setDataValue(
        'mvtBasisSelectionText',
        TransactionConstants.MIN_FIELD_MESSAGE,
      )
      rowNode.setDataValue('mvtBasisSelection', [])
    } else {
      if (
        rowNode.data.mvtBasisSelectionText ==
        TransactionConstants.NO_MARKET_VALUE_TRANSFER
      ) {
        rowNode.data.mvtBasisSelection.forEach(
          (mvtBasis: MvtBasisSelection) => {
            mvtBasis.marketValue = false
            mvtBasis.bookValue = true
          },
        )
      } else {
        const allMvBasisPrefix: string[] =
          rowNode.data.mvtBasisSelectionText.split(',')
        rowNode.data.mvtBasisSelection.forEach(
          (mvtBasis: MvtBasisSelection) => {
            mvtBasis.marketValue = allMvBasisPrefix.includes(
              mvtBasis.basis.prefix,
            )
            mvtBasis.bookValue = !allMvBasisPrefix.includes(
              mvtBasis.basis.prefix,
            )
          },
        )
      }
    }
  }

  @HostListener('click') dialogClicked() {
    if (isDialogComponentInActive(this.compId)) {
      activateDialog(this.activeInstances, this.compId, this.elRef)
    }
  }

  getComponentId() {
    return this.compId
  }

  copyHeaders() {
    const headers = this.gridApi?.columnApi
      .getAllDisplayedColumns()
      .filter(col => col.getColId() !== 'validityStatus')
      .map(col => col.getColDef().headerName)
    let headersTabSeparated = ''
    if (headers) headersTabSeparated = headers.join('\t')

    navigator.clipboard.writeText(headersTabSeparated)
    this.toastService.success('Copied to Clipboard')
  }
}
